import React, { useEffect, useState } from 'react';
import { api, auth } from '../api.ts';
import { FONT_UI } from '../theme';
import { mediaPublicUrl, viltPublicUrl } from '../karta/pinUtils';
import { useQrDataUrl } from '../karta/useQrDataUrl';

const STATUS_CFG: Record<string, { label: string; color: string }> = {
  fallt:   { label: 'Fällt',   color: '#E53935' },
  urtaget: { label: 'Urtaget', color: '#EE8F09' },
  hangd:   { label: 'Hängd',   color: '#8B4513' },
  styckad: { label: 'Styckad', color: '#61D836' },
};

export function ViltPublicPage({ pinId }: { pinId: string }) {
  const [pin, setPin] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const qrSrc = useQrDataUrl(viltPublicUrl(pinId));
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const user = auth.getUser();
  const token = auth.getAccessToken();

  useEffect(() => {
    api.falltViltPublic(pinId)
      .then(p => { setPin(p); setEvents(p?.falltViltEvents || []); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [pinId]);

  const handleStatus = async (status: string) => {
    if (!token) { alert('Logga in för att uppdatera status'); return; }
    setSubmitting(true);
    try {
      const ev = await api.updateFalltViltStatus(pinId, status);
      setEvents(prev => [...prev, ev]);
      setPin((p: any) => p ? { ...p, falltStatus: status } : p);
    } catch (e: any) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const handleComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      const ev = await api.addFalltViltComment(pinId, comment.trim());
      setEvents(prev => [...prev, ev]);
      setComment('');
    } catch (e: any) { alert(e.message); }
    finally { setSubmitting(false); }
  };

  const fmtTime = (iso: string) => {
    try { return new Date(iso).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
  };

  const eventLabel = (ev: any) => {
    if (ev.eventType === 'status_change') {
      const ns = STATUS_CFG[ev.newStatus || '']?.label || ev.newStatus || '';
      return `Status: ${ns}${ev.commentText ? ' — ' + ev.commentText : ''}`;
    }
    if (ev.eventType === 'comment') return ev.commentText || '';
    if (ev.eventType === 'move') return ev.commentText || 'Flytt';
    return ev.commentText || ev.eventType;
  };

  const bgStyle: React.CSSProperties = { minHeight: '100vh', background: '#0a1a08', fontFamily: FONT_UI, padding: 0 };
  const cardStyle: React.CSSProperties = { background: '#122210', borderRadius: 8, padding: 20, border: '1px solid rgba(78,89,76,0.35)', marginBottom: 14 };
  const lblStyle: React.CSSProperties = { color: '#989a98', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 5 };
  const inpStyle: React.CSSProperties = { width: '100%', background: '#1a2a1a', border: '1px solid rgba(78,89,76,0.35)', borderRadius: 4, padding: '8px 12px', color: '#f0ebe0', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' };

  return (
    <div style={bgStyle}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 36, height: 36, borderRadius: 5, background: 'rgba(238,143,9,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 20 }}>🦌</span>
          </div>
          <div>
            <div style={{ color: '#EE8F09', fontWeight: 700, fontSize: 13, letterSpacing: '0.05em' }}>QVENARI · FÄLLT VILT</div>
            <div style={{ color: '#989a98', fontSize: 12 }}>Spårning av fällt djur</div>
          </div>
        </div>

        {loading && <div style={{ color: '#989a98', textAlign: 'center', padding: 40 }}>Laddar...</div>}
        {err && <div style={{ color: '#ff6b6b', textAlign: 'center', padding: 20 }}>{err}</div>}
        {!loading && !pin && !err && <div style={{ color: '#989a98', textAlign: 'center', padding: 40 }}>Hittades inte</div>}

        {pin && (
          <>
            <div style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ color: '#f0ebe0', fontWeight: 700, fontSize: 22, marginBottom: 4 }}>{pin.name || pin.viltslag || 'Fällt vilt'}</div>
                  {pin.viltslag && (
                    <div style={{ color: '#989a98', fontSize: 14 }}>
                      {pin.viltslag}
                      {pin.kon && pin.kon !== 'Okänt' ? ` · ${pin.kon}` : ''}
                      {pin.alder && pin.alder !== 'Okänt' ? ` · ${pin.alder}` : ''}
                    </div>
                  )}
                  {pin.jagarEmail && <div style={{ color: '#6a6e6a', fontSize: 12, marginTop: 2 }}>Jägare: {pin.jagarEmail}</div>}
                </div>
                {(() => {
                  const sc = STATUS_CFG[pin.falltStatus || 'fallt'] || STATUS_CFG.fallt;
                  return <span style={{ background: `${sc.color}22`, color: sc.color, fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 10, border: `1px solid ${sc.color}44`, whiteSpace: 'nowrap' }}>{sc.label}</span>;
                })()}
              </div>

              {pin.photoUrls && pin.photoUrls.length > 0 && (
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 10 }}>
                  {pin.photoUrls.map((url: string, i: number) => (
                    <img key={i} src={mediaPublicUrl(url)} alt="Foto" style={{ height: 120, width: 'auto', borderRadius: 4, border: '1px solid rgba(78,89,76,0.35)', flexShrink: 0 }} />
                  ))}
                </div>
              )}

              <div style={{ color: '#6a6e6a', fontSize: 12 }}>
                Position: {pin.lat?.toFixed(5)}, {pin.lng?.toFixed(5)} · <a href={`https://maps.google.com/?q=${pin.lat},${pin.lng}`} target="_blank" rel="noopener noreferrer" style={{ color: '#EE8F09' }}>Öppna i kartan</a>
              </div>
              {pin.qregistryObjectId && <div style={{ color: '#6a6e6a', fontSize: 12, marginTop: 4 }}>Qregistry: {pin.qregistryObjectId}</div>}
            </div>

            <div style={cardStyle}>
              <div style={lblStyle}>QR-kod</div>
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <img
                  src={qrSrc}
                  alt="QR-kod"
                  style={{ width: 160, height: 160, borderRadius: 4 }}
                />
                <div style={{ color: '#6a6e6a', fontSize: 11, marginTop: 6 }}>{viltPublicUrl(pin.id)}</div>
              </div>
            </div>

            {token && (
              <div style={cardStyle}>
                <div style={lblStyle}>Uppdatera status</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {Object.entries(STATUS_CFG).filter(([k]) => k !== (pin.falltStatus || 'fallt')).map(([k, v]) => (
                    <button key={k} onClick={() => handleStatus(k)} disabled={submitting} style={{
                      padding: '7px 16px', borderRadius: 4, border: `1px solid ${v.color}`,
                      background: 'transparent', color: v.color, fontWeight: 600, cursor: 'pointer', fontSize: 13,
                      opacity: submitting ? 0.5 : 1,
                    }}>{v.label}</button>
                  ))}
                </div>
              </div>
            )}

            <div style={cardStyle}>
              <div style={lblStyle}>Händelselogg</div>
              {events.length === 0 ? (
                <div style={{ color: '#6a6e6a', fontSize: 13, marginTop: 8 }}>Inga händelser</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                  {events.map((ev: any) => {
                    const isStatus = ev.eventType === 'status_change';
                    const sc = isStatus ? (STATUS_CFG[ev.newStatus || ''] || null) : null;
                    return (
                      <div key={ev.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc ? sc.color : '#6a6e6a', marginTop: 4, flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#f0ebe0', fontSize: 13 }}>{eventLabel(ev)}</div>
                          <div style={{ color: '#6a6e6a', fontSize: 11, marginTop: 1 }}>{fmtTime(ev.createdAt)}{ev.authorEmail ? ` · ${ev.authorEmail}` : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={cardStyle}>
              <div style={lblStyle}>Lägg till kommentar</div>
              {!token && <div style={{ color: '#6a6e6a', fontSize: 13, marginTop: 8 }}>Logga in för att kommentera</div>}
              {token && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    value={comment} onChange={e => setComment(e.target.value)}
                    placeholder="Kommentar..."
                    style={inpStyle}
                    onKeyDown={e => e.key === 'Enter' && handleComment()}
                  />
                  <button onClick={handleComment} disabled={submitting || !comment.trim()} style={{
                    padding: '8px 16px', borderRadius: 4, border: 'none', background: '#EE8F09',
                    color: '#000', fontWeight: 700, cursor: 'pointer', fontSize: 13,
                    opacity: submitting || !comment.trim() ? 0.5 : 1, whiteSpace: 'nowrap',
                  }}>Skicka</button>
                </div>
              )}
            </div>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <a href="/" style={{ color: '#EE8F09', fontSize: 13, textDecoration: 'none' }}>← Tillbaka till Qvenari</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
